import { selectParams } from 'common/selectors';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Dropdown from './Dropdown';
import Item from './Item';
import equal from 'react-fast-compare';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useEffect } from 'react';

type SideBarItemProps = {
  folder: any;
  type: any;
  to: any;
  icon: any;
  className: string;
  buttonActions: any;
  zIndex: number;
  code: 'teamBundles' | 'privateBundles' | 'trialbooks';
  isLocked?: boolean;
  canUpdateContainer?: boolean;
};

const DropdownItem = React.memo((props: any) => {
  const { folder, folder1, folder2, folder3, folder4 } = useSelector(selectParams) as any;
  const {
    folder: { folders, code },
    to,
    isLocked,
    canUpdateContainer,
  } = props;

  const opened = useMemo(() => {
    return !!folders.find((x: any) =>
      [folder, folder1, folder2, folder3, folder4].includes(x.code),
    );
  }, [folders, folder, folder1, folder2, folder3, folder4]);

  return (
    <Dropdown
      {...props}
      to={`${to}/${code}`}
      opened={opened}
      isLocked={isLocked}
      canUpdateContainer={canUpdateContainer}
    />
  );
}, equal);

export default React.memo(
  (props: SideBarItemProps) => {
    const {
      folder: { folders, code },
      to,
      zIndex = 0,
      isLocked,
      canUpdateContainer,
    } = props;

    // Enable dragging only for bundles we want to drop into SearchInput
    const canDragBundle = ['team-bundle', 'private-bundle', 'trialbook'].includes(
      props.folder?.type,
    );

    const [{ isDragging }, dragRef, preview] = useDrag(
      () => ({
        type: 'BUNDLE',
        canDrag: () => canDragBundle,
        item: () => ({ id: props.folder?.id, name: props.folder?.name }),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      }),
      [props.folder, canDragBundle],
    );

    // Hide default drag preview so we can use the custom drag layer
    useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    return (
      <div
        className="case-sidebar-item"
        style={{
          zIndex,
          ...(isDragging && {
            opacity: 1,
            backgroundColor: '#4567e5',
            color: '#fff',
            borderRadius: 4,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            cursor: 'grabbing',
          }),
        }}
        ref={dragRef}
      >
        {folders && folders.length > 0 ? (
          <DropdownItem {...props} isLocked={isLocked} canUpdateContainer={canUpdateContainer} />
        ) : (
          <Item {...props} to={`${to}/${code}`} />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return equal(prevProps, nextProps);
  },
);
